import { useState, useEffect, useRef, memo } from 'react';

// The world of Onda in motion — a rotating montage of real beauty-at-work scenes
// (Pexels stock, free licence): the barber shop, the bride, threading, the salon,
// the artist rolling her kit into a hotel lobby, the stylist's chair.
// SELF-HOSTED: re-encoded to 720p/800kbps/faststart with poster frames, served
// from /opt/onda/media (see docs/HARDENING-2026-07-09.md — no Pexels hotlinking).
const CLIPS = [
  { src: '/media/hero/clip1.mp4', poster: '/media/hero/poster1.jpg' }, // NY stylist chair
  { src: '/media/hero/clip2.mp4', poster: '/media/hero/poster2.jpg' }, // barber shop
  { src: '/media/hero/clip3.mp4', poster: '/media/hero/poster3.jpg' }, // artist + kit, hotel
  { src: '/media/hero/clip4.mp4', poster: '/media/hero/poster4.jpg' }, // bridal makeup
  { src: '/media/hero/clip5.mp4', poster: '/media/hero/poster5.jpg' }, // eyebrow threading
  { src: '/media/hero/clip6.mp4', poster: '/media/hero/poster6.jpg' }, // nail salon
];

// Memoized — the landing page must never remount or repaint this layer.
// ONE persistent <video>: clips rotate by swapping src on the same element
// (a key= remount discarded the whole media pipeline per clip → black flash).
// During a swap the video dips to opacity 0 so the hero still underneath shows
// through instead of black; the next clip preloads while the current one plays.
function HeroMontage() {
  const videoRef = useRef(null);
  const preloadRef = useRef(null);
  const idxRef = useRef(0);
  const errorsRef = useRef(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    // Accessibility: honour reduced-motion — show the gradient hero only.
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => {
      mq.removeEventListener?.('change', onChange);
      if (preloadRef.current) {
        preloadRef.current.removeAttribute('src');
        preloadRef.current = null;
      }
    };
  }, []);

  if (reduced) return null;

  const preloadNext = () => {
    const next = CLIPS[(idxRef.current + 1) % CLIPS.length];
    const pre = preloadRef.current || document.createElement('video');
    pre.preload = 'auto';
    pre.muted = true;
    pre.src = next.src;
    preloadRef.current = pre;
  };

  const advance = (wasError) => {
    const v = videoRef.current;
    if (!v) return;
    if (wasError) {
      // A full lap of failures means the CDN is unreachable — stop, keep the still hero.
      errorsRef.current += 1;
      if (errorsRef.current >= CLIPS.length) return;
    } else {
      errorsRef.current = 0;
    }
    idxRef.current = (idxRef.current + 1) % CLIPS.length;
    v.style.opacity = '0';
    v.poster = CLIPS[idxRef.current].poster;
    v.src = CLIPS[idxRef.current].src;
    v.load();
    v.play().catch(() => {});
  };

  const handlePlaying = () => {
    const v = videoRef.current;
    if (v) v.style.opacity = '0.9'; // the 90/10 video/still hero blend
    errorsRef.current = 0;
    preloadNext();
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <video
        ref={videoRef}
        src={CLIPS[0].src}
        poster={CLIPS[0].poster}
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={() => advance(false)}
        onError={() => advance(true)}
        onPlaying={handlePlaying}
        className="w-full h-full object-cover object-center hero-montage-fade"
        style={{ opacity: 0 }}
      />
    </div>
  );
}

export default memo(HeroMontage);
